/**
 * Getting the caller's Firebase ID token, without racing Firebase's async init.
 *
 * Lifted verbatim out of WhatsAppTab.vue on 2026-09-02, when the WhatsApp wizard became a second
 * component that POSTs to the same `/wa-onboarding/my-*` endpoints. Those endpoints verify the
 * token server-side, so a component that gets this wrong does not degrade — it 401s and the screen
 * dies. That is not a thing to have two copies of.
 *
 * ⚠️ THE BUG THIS EXISTS TO PREVENT (observed live on dev, 2026-08-18): Firebase auth initializes
 * ASYNCHRONOUSLY after a hard page load, so on a direct deep-link (…/my-account?tab=whatsapp) a
 * mount-time fetch can outrun it — token null → Bearer-less GET → 401 → the error card on first
 * paint. `waitForAuthUser` waits for the first auth state instead of racing it. The timeout keeps a
 * broken auth from hanging the caller forever; it falls through to the 401 path, which every
 * caller renders as a recoverable error.
 *
 * NOT YET ADOPTED BY: src/pages/Gautam/components/WhatsAppOnboarding.vue, which still carries its
 * own copy. Folding it in is a safe follow-up, deliberately left out of the wizard change so an
 * operator-only surface is not touched by a customer-facing task.
 */

/** How long to wait for Firebase's first auth state before giving up (ms). */
const AUTH_INIT_TIMEOUT_MS = 8000;

/**
 * Resolve the current Firebase user, waiting out the async init.
 *
 * @param {object} auth the Firebase auth instance (`this.$store.state.getAuth()`)
 * @returns {Promise<object|null>} the user, or null when there is none / auth never initialized
 */
export function waitForAuthUser(auth) {
  if (!auth) return Promise.resolve(null);
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const timer = setTimeout(() => { unsub(); resolve(auth.currentUser); }, AUTH_INIT_TIMEOUT_MS);
    const unsub = auth.onAuthStateChanged((user) => {
      clearTimeout(timer);
      unsub();
      resolve(user);
    });
  });
}

/**
 * The caller's Firebase ID token, or null.
 *
 * Never throws: every caller here is about to make a request that has its own error path, and a
 * throw from the token step would take out the request's error handling with it.
 */
export async function getIdToken(auth, forceRefresh = false) {
  try {
    const user = await waitForAuthUser(auth);
    if (user) return await user.getIdToken(forceRefresh);
  } catch (e) {
    console.log('WA: could not get ID token', e);
  }
  return null;
}
